/* eslint-disable no-restricted-imports */
import merge from 'lodash.merge';
import React, { createContext, useContext, useMemo } from 'react';
import {
  ACCENT_LIGHT_OPACITY,
  ACCENT_MEDIUM_OPACITY,
  theme as baseTheme,
  COLORS,
} from '@/styles';
import { lighten } from '@/utils/colors';
import { StyleSheet } from 'react-native';
import { SupplierTheme } from '../../partners/config/types';
import { tokens, Tokens } from '@usepepper/sprout-kit';
import { useDemoSettingsContext } from './demo-settings-context';

export type Theme = SupplierTheme & {
  colors: typeof COLORS;
  sk: Tokens;
  // TODO - extend with other theme properties
};

export interface ThemeContextType {
  theme: Theme;
}

export const ThemeContext = createContext<ThemeContextType | null>(null);

interface Props {
  children?: React.ReactNode;
}

export const ThemeContextProvider: React.FC<Props> = ({ children }) => {
  const { demoSettings } = useDemoSettingsContext();

  const themeOverrides = useMemo(() => {
    const accentColor = demoSettings?.accentColor;
    return {
      colors: {
        ...(accentColor
          ? {
              accent: accentColor,
              accentLight: lighten(accentColor, ACCENT_LIGHT_OPACITY),
              accentMedium: lighten(accentColor, ACCENT_MEDIUM_OPACITY),
            }
          : {}),
        accentText: demoSettings?.accentTextColor,
      },
      images: {
        welcomeLogoUrl: demoSettings?.welcomeLogoUrl,
      },
    };
  }, [demoSettings]);

  const value = useMemo<ThemeContextType>(() => {
    return {
      theme: merge(
        {},
        baseTheme,
        { colors: COLORS },
        { sk: tokens },
        themeOverrides,
      ),
    };
  }, [themeOverrides]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext requires a ThemeContextProvider');
  }

  return context;
};

export const useTheme = () => {
  const { theme } = useThemeContext();
  return theme;
};

export function createStyles<T extends StyleSheet.NamedStyles<T>>(
  fn: (theme: Theme) => T,
) {
  return () => {
    const { theme } = useThemeContext();
    return useMemo<[T, Theme]>(
      () => [StyleSheet.create(fn(theme)), theme],
      [theme],
    );
  };
}
